/**
 * Articles (blog) — one markdown file per post under `content/articles/`.
 *
 * Publishing a post is a content-only change: drop a `.md` file in that folder
 * with the frontmatter block below and it appears at `/articles/<filename>`.
 * No edit here is required — the glob is resolved at build time.
 *
 * ```md
 * ---
 * title: Triggering Terms in Mortgage Ads
 * date: 2026-08-11
 * summary: One sentence for the index card and the social preview.
 * author: Halo Compliance
 * image: /images/articles/triggering-terms.png   # optional
 * imageAlt: Description for screen readers       # required when image is set
 * draft: true                                    # optional, defaults to false
 * ---
 * ```
 *
 * `image` is the **card**: it is the `og:image` LinkedIn and friends read, and
 * the thumbnail on `/articles`. It is not rendered on the post itself — the
 * card carries the headline as artwork, so showing it under the real `<h1>`
 * prints the title twice. Make it 1200x630 (the ratio those previews crop to)
 * and a **PNG or JPG** — social platforms will not render an SVG card.
 *
 * `draft: true` keeps a post out of the index and off its own URL — the review
 * lane. Flip it to `false` (or drop the line) when it is cleared to publish.
 */

export type Article = {
  slug: string
  title: string
  /** ISO `YYYY-MM-DD`. Sorted on, and rendered as the dateline. */
  date: string
  summary: string
  author: string
  image?: string
  imageAlt?: string
  draft: boolean
  /** Markdown body with the frontmatter block stripped. */
  body: string
}

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Minimal frontmatter reader: flat `key: value` pairs, optional surrounding
 * quotes, `#` comments. Deliberately not a YAML parser — the schema above is
 * the whole contract, and a real parser is a dependency the posts do not earn.
 */
function parseFrontmatter(raw: string): Record<string, string> {
  const fields: Record<string, string> = {}
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue
    const separator = trimmed.indexOf(':')
    if (separator === -1) continue
    const key = trimmed.slice(0, separator).trim()
    let value = trimmed.slice(separator + 1).trim()
    value = value.replace(/\s+#.*$/, '').trim()
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1)
    }
    fields[key] = value
  }
  return fields
}

/**
 * A malformed post must not take the site down. In dev it throws, so the author
 * sees it in preview the moment they save; in prod the post is dropped and the
 * rest of the site serves. The route-meta stamp step evaluates this module in
 * dev mode during `vite build`, so a malformed post also fails the build — CI
 * green is evidence the frontmatter parses.
 */
function reject(slug: string, reason: string): null {
  const message = `Article "${slug}": ${reason}`
  if (import.meta.env.DEV) throw new Error(message)
  console.error(message)
  return null
}

function toArticle(path: string, source: string): Article | null {
  const slug = path.split('/').pop()!.replace(/\.md$/, '')
  const match = FRONTMATTER.exec(source)
  if (!match) return reject(slug, 'missing the `---` frontmatter block')

  const fields = parseFrontmatter(match[1])
  const body = source.slice(match[0].length).trim()

  for (const required of ['title', 'date', 'summary', 'author'] as const) {
    if (!fields[required]) return reject(slug, `frontmatter is missing \`${required}\``)
  }
  if (!ISO_DATE.test(fields.date)) {
    return reject(slug, `date "${fields.date}" is not \`YYYY-MM-DD\``)
  }
  if (!body) return reject(slug, 'has no body below the frontmatter')
  if (fields.image && !fields.imageAlt) {
    return reject(slug, 'sets `image` without `imageAlt` (screen readers need it)')
  }

  return {
    slug,
    title: fields.title,
    date: fields.date,
    summary: fields.summary,
    author: fields.author,
    image: fields.image || undefined,
    imageAlt: fields.imageAlt || undefined,
    draft: fields.draft === 'true',
    body,
  }
}

const sources = import.meta.glob('../content/articles/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

/** Every well-formed post, drafts included, newest first. */
export const ALL_ARTICLES: Article[] = Object.entries(sources)
  .map(([path, source]) => toArticle(path, source))
  .filter((article): article is Article => article !== null)
  .sort((a, b) => (a.date === b.date ? a.slug.localeCompare(b.slug) : b.date.localeCompare(a.date)))

/**
 * Drafts stay visible in dev so they can be previewed at their real URL, and are
 * withheld from the built site.
 */
export const PUBLISHED_ARTICLES: Article[] = ALL_ARTICLES.filter(
  (article) => import.meta.env.DEV || !article.draft,
)

export function getArticle(slug: string): Article | undefined {
  return PUBLISHED_ARTICLES.find((article) => article.slug === slug)
}

/** `2026-08-11` -> `August 11, 2026`, parsed as UTC so the date never shifts a day. */
export function formatArticleDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}
